/**
 * 把高频事件合并到下一帧执行一次，避免 SSE 每个事件触发一次 React setState 抖动。
 *
 * 与 streamTypewriter 的区别：streamTypewriter 控制"按字符渲染"的视觉节奏，
 * frameThrottle 只做"每帧最多一次刷新"的批处理，适用于 reasoning / tool 步骤、
 * 翻译 token 等不需要逐字打字效果但需要去抖的场景。
 *
 * 用法：
 *   const flush = createFrameThrottle(() => setItems(buildSnapshot()));
 *   sse.onEvent = (e) => { mutateLocalState(e); flush.schedule(); };
 *   await sse; flush.flush();                  // 正常结束 → 立刻刷一帧确保最终态
 *   abortController.signal.addEventListener("abort", () => flush.cancel());
 */
export type FrameThrottle = {
  schedule: () => void;
  flush: () => void;
  cancel: () => void;
};

export function createFrameThrottle(callback: () => void, enabled = true): FrameThrottle {
  // 即时模式：关闭帧节流，每次 schedule 立即执行回调，按上游节奏原速刷新。
  if (!enabled) {
    let cancelled = false;
    return {
      schedule() {
        if (cancelled) return;
        callback();
      },
      flush() {
        if (cancelled) return;
        callback();
      },
      cancel() {
        cancelled = true;
      },
    };
  }

  let scheduled = false;
  let cancelled = false;

  const run = () => {
    scheduled = false;
    if (cancelled) return;
    callback();
  };

  return {
    schedule() {
      if (scheduled || cancelled) return;
      scheduled = true;
      requestAnimationFrame(run);
    },
    flush() {
      scheduled = false;
      if (cancelled) return;
      callback();
    },
    cancel() {
      cancelled = true;
      scheduled = false;
    },
  };
}
